/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Check,
  ExternalLink,
  FilePenLine,
  Globe,
  Image,
  Link as LinkIcon,
  Loader2,
  Pencil,
  Play,
  Presentation,
  RefreshCw,
  Rotate3d,
  Ruler,
  ScrollText,
  Table2,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';

import { Text } from '@/shared/ui';

import type { LaunchMaterial } from '../../types';

/*
 * Every value below is authored inline rather than as an arbitrary Tailwind
 * class. Two reasons, both learned the hard way in this repo: a freshly
 * written arbitrary class is not always generated (`py-[var(--space-1-5)]`
 * silently produced no rule), and the prototype itself authors these as
 * inline styles — matching it literally is what finally closed the gap.
 */

/** Category icons, matching the prototype's choices. */
const CATEGORY_ICON: Record<string, typeof BookOpen> = {
  price_table: Table2,
  sales_book: BookOpen,
  floor_plans: Ruler,
  specs: ScrollText,
  contract_template: FilePenLine,
  media: Image,
};

/** Provider icons, matching the prototype's PROV table. */
const PROVIDER_ICON: Record<string, typeof BookOpen> = {
  youtube: Play,
  vimeo: Play,
  drive: Globe,
  slides: Presentation,
  matterport: Rotate3d,
};

/** Human size, in the unit a person would say out loud. */
function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/** Clock time for a video duration. */
function formatDuration(seconds?: number | null): string {
  if (!seconds) return '';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Day and month, the way the prototype stamps a material. */
function formatDay(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/** Turns `2-dormitorios` back into something a person reads. */
function typologyLabel(keys: string[]): string {
  return keys.map((key) => key.replace(/-/g, ' ')).join(', ');
}

interface MaterialCardProps {
  material: LaunchMaterial;
  canManage: boolean;
  onOpen: (material: LaunchMaterial) => void;
  onDelete?: (material: LaunchMaterial) => void;
  onRecheck?: () => void;
  onReplace?: () => void;
  /** Resolves once the new name is stored; rejects to keep the field open. */
  onRename?: (material: LaunchMaterial, title: string) => Promise<void>;
}

/**
 * Why a link stopped working, in words the reader can act on.
 *
 * Sits at the foot of the card, separated by a rule rather than floating in
 * its own box: the failure belongs to this material, and a second bordered
 * rectangle inside a bordered card reads as two cards.
 */
function BrokenNotice({ material, canManage, onRecheck, onReplace }: {
  material: LaunchMaterial;
  canManage: boolean;
  onRecheck?: () => void;
  onReplace?: () => void;
}) {
  const { t } = useTranslation('features/launches');
  const status = material.embed_status ?? 'ok';
  if (status === 'ok') return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        background: 'var(--launch-material-broken-bg)',
        borderTop: '1px solid var(--launch-material-broken-border)',
        color: 'var(--launch-material-broken-fg)',
      }}
    >
      <Text
        as="span"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-semibold)',
        }}
      >
        <Unlink size={15} />
        {t(`materials.broken.${status}.title`)}
      </Text>

      <Text
        as="span"
        style={{ fontSize: 'var(--text-small)', lineHeight: 'var(--leading-normal)' }}
      >
        {t(`materials.broken.${status}.${canManage ? 'manager' : 'broker'}`)}
      </Text>

      {canManage && (
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {onRecheck && (
            <button
              type="button"
              onClick={onRecheck}
              style={{
                height: 'var(--space-9)',
                padding: '0 var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--launch-material-broken-border)',
                background: 'var(--surface-raised)',
                color: 'var(--text-primary)',
                fontSize: 'var(--text-small)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1-5)',
              }}
            >
              <RefreshCw size={13} />
              {t('materials.broken.recheck')}
            </button>
          )}

          {onReplace && (
            <button
              type="button"
              onClick={onReplace}
              style={{
                height: 'var(--space-9)',
                padding: '0 var(--space-3)',
                border: 'none',
                background: 'none',
                color: 'var(--launch-material-broken-fg)',
                fontSize: 'var(--text-small)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
              }}
            >
              {t('materials.replace')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The material's name, editable in place.
 *
 * A pencil beside the title rather than a form behind a menu: renaming is
 * the correction a manager makes seconds after publishing — "Tabela
 * atualizada (1).pdf" is the filename, not the name a broker should read —
 * and a round trip through a dialog for one line of text is a wall where a
 * typo-fix belongs.
 */
export function MaterialTitle({ material, canRename, onRename, t }: {
  material: LaunchMaterial;
  canRename: boolean;
  onRename?: (material: LaunchMaterial, title: string) => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(material.title);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) input.current?.select(); }, [editing]);

  const open = () => { setDraft(material.title); setFailed(false); setEditing(true); };
  const cancel = () => { setEditing(false); setFailed(false); };

  const commit = () => {
    const next = draft.trim();
    if (!next || !onRename) { cancel(); return; }
    if (next === material.title) { cancel(); return; }
    setSaving(true);
    setFailed(false);
    onRename(material, next)
      .then(() => setEditing(false))
      .catch(() => setFailed(true))
      .finally(() => setSaving(false));
  };

  if (!editing) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)', minWidth: 0 }}>
        <Text
          as="span"
          style={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-body)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--text-primary)',
          }}
        >
          {material.title}
        </Text>

        {canRename && onRename && (
          <button
            type="button"
            onClick={open}
            aria-label={t('materials.rename')}
            title={t('materials.rename')}
            data-testid="material-rename"
            style={{
              width: 'var(--space-7)',
              height: 'var(--space-7)',
              flexShrink: 0,
              border: 'none',
              background: 'none',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
            }}
          >
            <Pencil size={13} />
          </button>
        )}
      </span>
    );
  }

  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
        <input
          ref={input}
          value={draft}
          disabled={saving}
          aria-label={t('materials.renameLabel')}
          data-testid="material-rename-input"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          style={{
            flex: 1,
            minWidth: 0,
            height: 'var(--space-9)',
            padding: '0 var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-medium)',
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)',
            outline: 'none',
          }}
        />

        <button
          type="button"
          onClick={commit}
          disabled={saving}
          aria-label={t('materials.renameSave')}
          data-testid="material-rename-save"
          style={{
            width: 'var(--space-9)',
            height: 'var(--space-9)',
            flexShrink: 0,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'var(--button-primary-bg)',
            color: 'var(--button-primary-text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        </button>

        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          aria-label={t('materials.renameCancel')}
          style={{
            width: 'var(--space-9)',
            height: 'var(--space-9)',
            flexShrink: 0,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'none',
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={14} />
        </button>
      </span>

      {failed && (
        <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--launch-danger-fg)' }}>
          {t('materials.renameFailed')}
        </Text>
      )}
    </span>
  );
}

/**
 * One material in the library.
 *
 * A preview plate on top of a body, rather than a padded row: the broker
 * scanning this grid in a stand recognises a floor plan by its shape long
 * before they read its name. Embedded content keeps the dashed border and
 * says where it lives — it can disappear without anyone here touching it.
 */
export function MaterialCard({
  material,
  canManage,
  onOpen,
  onDelete,
  onRecheck,
  onReplace,
  onRename,
}: MaterialCardProps) {
  const { t } = useTranslation('features/launches');
  const isEmbed = material.source === 'embed';
  const broken = isEmbed && (material.embed_status ?? 'ok') !== 'ok';
  const typologies = material.typology_keys ?? [];

  /* Dashed for embeds, solid for files — on the frame and on the plate's
     rule, so the whole card reads as one outline. */
  const edge = isEmbed ? 'dashed' : 'solid';
  const borderColor = isEmbed
    ? 'var(--launch-material-external-border)'
    : 'var(--border-medium)';

  const providerName = isEmbed
    ? t(`materials.providers.${material.provider ?? 'drive'}`)
    : t('materials.hostedHere');

  const Icon = isEmbed
    ? (PROVIDER_ICON[material.provider ?? 'drive'] ?? Globe)
    : (CATEGORY_ICON[material.category] ?? BookOpen);

  const isImage = (material.mime_type ?? '').startsWith('image/');
  const meta = isEmbed
    ? [providerName, formatDuration(material.duration_seconds) || material.provider_title]
        .filter(Boolean).join(' · ')
    : [
        isImage ? t('materials.image') : 'PDF',
        material.page_count ? t('materials.pageCount', { count: material.page_count }) : '',
        formatSize(material.size_bytes),
      ].filter(Boolean).join(' · ');

  const updated = formatDay(material.updated_at);

  return (
    <div
      data-testid="launch-material-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 'var(--radius-lg)',
        border: `1px ${edge} ${borderColor}`,
        background: 'var(--surface-card)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(material)}
        style={{
          height: 'var(--space-24)',
          border: 'none',
          borderBottom: `1px ${edge} ${borderColor}`,
          /* The hatch stands in for a thumbnail the backend does not render
             yet; it reads as "a document lives here" without pretending to
             be the document. */
          background:
            'repeating-linear-gradient(135deg,var(--surface-card) 0 10px,var(--surface-tertiary) 10px 11px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          cursor: 'pointer',
          fontSize: 'var(--text-meta)',
          color: 'var(--text-secondary)',
        }}
      >
        <Icon size={18} />
        {providerName}
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
          padding: 'var(--space-3)',
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-0-5)',
          }}
        >
          <MaterialTitle
            material={material}
            canRename={canManage}
            onRename={onRename}
            t={t}
          />

          <Text
            as="span"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}
          >
            {[meta, updated && t('materials.updatedAt', { date: updated })]
              .filter(Boolean).join(' · ')}
          </Text>

          {isEmbed && (
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
              }}
            >
              <ExternalLink size={12} />
              {t('materials.hostedOutside')}
            </Text>
          )}

          {typologies.length > 0 && (
            <Text
              as="span"
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
              }}
            >
              <LinkIcon size={12} />
              {t('materials.appearsInUnits', { typologies: typologyLabel(typologies) })}
            </Text>
          )}
        </div>

        {canManage && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(material)}
            aria-label={t('materials.delete')}
            style={{
              width: 'var(--space-9)',
              height: 'var(--space-9)',
              flexShrink: 0,
              border: 'none',
              background: 'none',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-tertiary)',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      {broken && (
        <BrokenNotice
          material={material}
          canManage={canManage}
          onRecheck={onRecheck}
          onReplace={onReplace}
        />
      )}
    </div>
  );
}
